import React, { useMemo } from "react";

import StructuresMap from "./structures_map.svg?react";
import { TEAM_COLORS } from "@shared/utils";
import { useSnapshot } from "valtio";
import { uiState } from "@shared/states/uiStates";
import { useMapFrameSnap } from "@shared/Map/frame_hooks";
import { RIFT_SIZE } from "@shared/Map/Map";
import all_buildings from "./buildings.json";
import { Position, TeamId } from "@shared/types/riot";
import { setOverlayObject } from "@shared/Map/actions";
import { deepmerge } from "deepmerge-ts";

interface StructuresPanelProps {}

const StructuresPanel: React.FC<StructuresPanelProps> = (props) => {
	const uiSnap = useSnapshot(uiState);
	const frame = useMapFrameSnap(uiSnap.currentFrameIndex);

	const buildings = Object.values(all_buildings);
	const towers = buildings.filter(
		(b) => b.buildingType !== "NEXUS" && b.buildingType !== "INHIBITOR",
	);
	const inhibs = buildings.filter((b) => b.buildingType === "INHIBITOR");

	const mergedBuildings = useMemo(() => {
		return Object.values(deepmerge(all_buildings, frame.buildings?.objects || {}));
	}, [frame.buildings?.objects]);

	const blueBuildings = useMemo(() => {
		return mergedBuildings.filter((b) => b.teamId === 100);
	}, [mergedBuildings]);

	const redBuildings = useMemo(() => {
		return mergedBuildings.filter((b) => b.teamId === 200);
	}, [mergedBuildings]);

	const handleShowAll = (team: TeamId) => () => {
		const buildings = team === 100 ? blueBuildings : redBuildings;
		const isAllShown = buildings.every((b) => b.life > 0);
		for (const b of buildings) {
			const fullLife = b.buildingType === "OUTER_TURRET" ? 5 : 1;
			const newLife = isAllShown ? 0 : fullLife;

			setOverlayObject(uiState.currentFrameIndex, "buildings", b.id, {
				id: b.id,
				life: newLife,
			});
		}
	};

	const allBlueShown = blueBuildings.every((b) => b.life > 0);
	const allRedShown = redBuildings.every((b) => b.life > 0);

	return (
		<div>
			<div
				className={
					"p-2 bg-slate-600 cursor-pointer border-b border-slate-700 text-white font-bold flex justify-between"
				}
			>
				Structures
			</div>
			<div>
				<svg width={367} height={367} viewBox={`0 0 ${RIFT_SIZE} ${RIFT_SIZE}`}>
					<StructuresMap />
					{towers.map((t) => (
						<MiniTower
							key={t.id}
							towerId={t.id}
							laneType={t.lane}
							turretType={t.buildingType}
							position={t.position}
							teamId={t.teamId as TeamId}
							life={frame.buildings?.objects[t.id]?.life || 0}
						/>
					))}
					{inhibs.map((t) => (
						<MiniInhib
							key={t.id}
							inhibId={t.id}
							position={t.position}
							teamId={t.teamId as TeamId}
							life={frame.buildings?.objects[t.id]?.life || 0}
						/>
					))}
				</svg>
			</div>
			<div className={"flex"}>
				<div className={"flex-1"}>
					<label className={"select-none cursor-pointer m-4 text-xl  text-white"}>
						<input
							type={"checkbox"}
							className={"m-2"}
							checked={allBlueShown}
							onChange={handleShowAll(100)}
						/>
						All Blue
					</label>
				</div>
				<div className={"flex-1"}>
					<label className={"select-none cursor-pointer m-4 text-xl text-white"}>
						<input
							type={"checkbox"}
							className={"m-2"}
							checked={allRedShown}
							onChange={handleShowAll(200)}
						/>
						All Red
					</label>
				</div>
			</div>
		</div>
	);
};

interface MiniTowerProps {
	position: Position;
	teamId: TeamId;
	towerId: string;
	laneType: string;
	turretType: string;
	life: number;
}

const MiniTower = (props: MiniTowerProps) => {
	const { teamId, position } = props;
	const color = TEAM_COLORS[teamId];
	const size = 600;
	const hsize = size / 2;

	const handleClick = () => {
		const fullLife = props.turretType === "OUTER_TURRET" ? 5 : 1;
		const newLife = props.life > 0 ? props.life - 1 : fullLife;

		setOverlayObject(uiState.currentFrameIndex, "buildings", props.towerId, {
			id: props.towerId,
			life: newLife,
		});
	};

	return (
		<>
			<g transform={`translate(-${hsize},-${hsize})`}>
				<g transform={`translate(${position.x},${position.y})`} onClick={handleClick}>
					<g
						transform={`translate(${hsize},${hsize}) rotate(45) translate(-${hsize},-${hsize})`}
					>
						<rect
							x={0}
							y={0}
							opacity={props.life > 0 ? 1 : 0.3}
							width={size}
							height={size}
							strokeWidth={50}
							stroke={"white"}
							className={"cursor-pointer"}
							fill={color}
						></rect>
					</g>
					{props.life > 0 && props.turretType === "OUTER_TURRET" && (
						<text
							fontSize={450}
							x={160}
							y={470}
							width={500}
							fill={"white"}
							stroke={"white"}
						>
							{props.life}
						</text>
					)}
				</g>
			</g>
		</>
	);
};

interface MiniInhibProps {
	position: Position;
	teamId: TeamId;
	inhibId: string;
	life: number;
}

const MiniInhib = (props: MiniInhibProps) => {
	const { teamId, position } = props;
	const color = TEAM_COLORS[teamId];

	const handleClick = () => {
		const newLife = props.life === 1 ? 0 : 1;
		setOverlayObject(uiState.currentFrameIndex, "buildings", props.inhibId, {
			id: props.inhibId,
			life: newLife,
		});
	};
	return (
		<g transform={`translate(${position.x},${position.y})`}>
			<circle
				cx={0}
				onClick={handleClick}
				cy={0}
				r={300}
				opacity={props.life > 0 ? 1 : 0.3}
				strokeWidth={50}
				stroke={"white"}
				className={"cursor-pointer"}
				fill={color}
			></circle>
		</g>
	);
};

export { StructuresPanel };
